import React, { useState } from 'react';
import { FiDollarSign, FiCreditCard, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import PortalHeader from '../../components/Portal/PortalHeader';
import { usePortalInvoices } from '../../hooks/portal/usePortal';
import { payPortalInvoice } from '../../services/portal/portalApi';
import './PagarFactura.scss';

const PagarFactura = () => {
  const { invoices, summary, loading, refetch } = usePortalInvoices();
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSelectInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.balance || invoice.total);
    setError(null);
    setSuccess(false);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!selectedInvoice) {
      setError('Selecciona una factura para pagar');
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError('El monto del pago debe ser mayor a 0');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const paymentData = {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        payment_date: new Date().toISOString().split('T')[0], // Fecha actual en formato YYYY-MM-DD
        reference_number: paymentReference || undefined,
        notes: `Pago registrado desde portal del cliente`
      };

      await payPortalInvoice(selectedInvoice.id, paymentData);

      setSuccess(true);
      setSelectedInvoice(null);
      setPaymentAmount('');
      setPaymentReference('');

      // Refresh invoices list
      setTimeout(() => {
        refetch();
        setSuccess(false);
      }, 2000);

    } catch (err) {
      setError(err.message || 'Error al procesar el pago');
      console.error('Error processing payment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="portal-page">
        <PortalHeader />
        <div className="portal-page__loading">Cargando facturas...</div>
      </div>
    );
  }

  // Filter only unpaid and partially paid invoices
  const unpaidInvoices = invoices?.filter(inv =>
    inv.payment_status === 'unpaid' || inv.payment_status === 'partial'
  ) || [];

  return (
    <div className="portal-page pagar-factura">
      <PortalHeader />

      <div className="portal-page__container">
        <h1 className="portal-page__title">
          <FiDollarSign /> Pagar Facturas
        </h1>

        {summary && (
          <div className="payment-summary">
            <div className="summary-card outstanding">
              <h3>Total Pendiente</h3>
              <p className="amount">${summary.total_outstanding?.toFixed(2)}</p>
            </div>
            <div className="summary-card overdue">
              <h3>Facturas Vencidas</h3>
              <p className="amount">${summary.overdue_amount?.toFixed(2)}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="success-message">
            <FiCheckCircle />
            <span>Pago registrado exitosamente</span>
          </div>
        )}

        {error && (
          <div className="error-message">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        <div className="payment-content">
          {/* Lista de facturas pendientes */}
          <div className="invoices-section">
            <h2 className="section-title">Facturas Pendientes</h2>

            {unpaidInvoices.length > 0 ? (
              <div className="invoices-list">
                {unpaidInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className={`invoice-item ${selectedInvoice?.id === invoice.id ? 'selected' : ''}`}
                    onClick={() => handleSelectInvoice(invoice)}
                  >
                    <div className="invoice-header">
                      <span className="invoice-number">#{invoice.number}</span>
                      <span className={`invoice-status status--${invoice.payment_status.toLowerCase()}`}>
                        {invoice.is_overdue ? 'Vencida' : 'Pendiente'}
                      </span>
                    </div>
                    <div className="invoice-details">
                      <div className="detail-row">
                        <span className="label">Fecha:</span>
                        <span>{new Date(invoice.invoice_date).toLocaleDateString()}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Vencimiento:</span>
                        <span>{new Date(invoice.due_date).toLocaleDateString()}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Total:</span>
                        <span className="total-amount">${invoice.total?.toFixed(2)}</span>
                      </div>
                      {invoice.balance && invoice.balance !== invoice.total && (
                        <div className="detail-row">
                          <span className="label">Pendiente:</span>
                          <span className="balance-amount">${invoice.balance?.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-invoices">
                <FiCheckCircle />
                <p>No tienes facturas pendientes de pago</p>
              </div>
            )}
          </div>

          {/* Formulario de pago */}
          <div className="payment-form-section">
            <h2 className="section-title">
              <FiCreditCard /> Registrar Pago
            </h2>

            {selectedInvoice ? (
              <form className="payment-form" onSubmit={handleSubmitPayment}>
                <div className="selected-invoice-info">
                  <h3>Factura seleccionada</h3>
                  <p className="invoice-number">#{selectedInvoice.number}</p>
                  <p className="invoice-total">Total: ${selectedInvoice.total?.toFixed(2)}</p>
                </div>

                <div className="form-group">
                  <label htmlFor="payment_amount">
                    Monto a pagar <span className="required">*</span>
                  </label>
                  <div className="input-with-icon">
                    <span className="icon">$</span>
                    <input
                      type="number"
                      id="payment_amount"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      step="0.01"
                      min="0.01"
                      max={selectedInvoice.balance || selectedInvoice.total}
                      required
                    />
                  </div>
                  <small className="helper-text">
                    Máximo: ${(selectedInvoice.balance || selectedInvoice.total)?.toFixed(2)}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="payment_method">
                    Método de pago <span className="required">*</span>
                  </label>
                  <select
                    id="payment_method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    required
                  >
                    <option value="bank_transfer">Transferencia Bancaria</option>
                    <option value="cash">Efectivo</option>
                    <option value="check">Cheque</option>
                    <option value="credit_card">Tarjeta de Crédito</option>
                    <option value="debit_card">Tarjeta de Débito</option>
                    <option value="electronic">Pago Electrónico</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="payment_reference">
                    Número de referencia
                  </label>
                  <input
                    type="text"
                    id="payment_reference"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="Número de confirmación o referencia"
                  />
                  <small className="helper-text">
                    Opcional: Ingresa el número de referencia de tu transacción
                  </small>
                </div>

                <button
                  type="submit"
                  className="submit-payment-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Procesando...' : 'Registrar Pago'}
                </button>

                <p className="form-note">
                  Tu pago será revisado y aplicado a tu factura en un plazo de 24-48 horas.
                </p>
              </form>
            ) : (
              <div className="no-selection">
                <FiCreditCard />
                <p>Selecciona una factura de la lista para registrar un pago</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagarFactura;
